import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { PageHeader } from '@/common-components/layout';
import { DataGrid } from '@/common-components/tables';
import ParityBar from '@/common-components/parity-bar/parity-bar';
import { useDashboardContext } from '@/common-hooks/use-dashboard-context';
import type { 
  HungJob, 
  EarlyFailureJob, 
  LowParityJob, 
  OrganizationDetail,
  OrgPairAnalyticsItem,
} from '@/common-types';

export default function SearchDetail() {
  const { type, id } = useParams<{ type: string; id: string }>();
  const navigate = useNavigate();
  const { data: dashboardData, loading } = useDashboardContext();

  if (!type || !id) {
    return (
      <Box>
        <Alert severity="error">Invalid search detail parameters</Alert>
      </Box>
    );
  }

  const findEntity = () => {
    if (!dashboardData) return null;

    switch (type) {
      case 'job': {
        // Search in hung jobs
        const hungJob = dashboardData.metrics.hungJobs?.find((j) => j.transactionId === id);
        if (hungJob) return { type: 'job', data: hungJob, jobType: 'hung' };

        // Search in early failure jobs
        const failedJob = dashboardData.metrics.earlyFailureJobs?.find((j) => j.transactionId === id);
        if (failedJob) return { type: 'job', data: failedJob, jobType: 'failed' };

        // Search in low parity jobs
        const lowParityJob = dashboardData.metrics.lowParityJobs?.find((j) => j.transactionId === id);
        if (lowParityJob) return { type: 'job', data: lowParityJob, jobType: 'lowParity' };
        break;
      }
      case 'organization': {
        // Search in organization details
        const org = dashboardData.metrics.activeCustomerOrgs?.organizationDetails?.find(
          (o) => o.orgId === id
        );
        if (org) return { type: 'organization', data: org };

        // Search in org pairs
        const pair = dashboardData.metrics.orgPairAnalytics?.items?.find(
          (p) => `${p.sourceOrgId}-${p.destinationOrgId}` === id
        );
        if (pair) return { type: 'orgPair', data: pair };
        break;
      }
    }
    return null;
  };

  const entity = findEntity();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!entity) {
    return (
      <Box>
        <PageHeader
          title="Not Found"
          subtitle="The requested item could not be found"
          actions={
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              variant="outlined"
              size="small"
            >
              Back to Dashboard
            </Button>
          }
        />
        <Alert severity="error" sx={{ mt: 2 }}>
          The {type} with ID "{id}" was not found in the current dashboard data.
        </Alert>
      </Box>
    );
  }


  const renderJobDetail = (job: HungJob | EarlyFailureJob | LowParityJob, jobType: string) => {
    return (
      <Stack gap={2}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <WorkIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h5">
                  {jobType === 'hung' ? 'Hung Job' : jobType === 'failed' ? 'Failed Job' : 'Low Parity Job'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Transaction ID: {job.transactionId}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary">
                  Transaction ID
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mt: 0.5 }}
                >
                  {job.transactionId}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Source Organization
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {job.sourceOrgName}
                </Typography>
                {job.sourceOrgId && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {job.sourceOrgId}
                  </Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Destination Organization
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {job.destinationOrgName}
                </Typography>
                {job.destinationOrgId && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {job.destinationOrgId}
                  </Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Started
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {new Date(job.dateStarted).toLocaleString()}
                </Typography>
              </Grid>
              {'status' in job && job.status && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={job.status} size="small" color="error" />
                  </Box>
                </Grid>
              )}
              {'orgParityPercentage' in job && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Parity Percentage
                  </Typography>
                  <Box sx={{ mt: 0.5, minWidth: 160 }}>
                    <ParityBar
                      copied={parseFloat(job.orgParityPercentage)}
                      total={100}
                      isRunning={false}
                      totalBlocks={10}
                      showLoaderWhenRunning={false}
                    />
                  </Box>
                </Grid>
              )}
              {'failedSteps' in job && job.failedSteps && (
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">
                    Failed Steps
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {job.failedSteps.map((step, idx) => (
                      <Chip key={idx} label={step} size="small" color="error" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              )}
              {'successfullSteps' in job && job.successfullSteps && (
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">
                    Completed Steps
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {job.successfullSteps.map((step, idx) => (
                      <Chip key={idx} label={step} size="small" color="success" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Stack>
    );
  };

  const renderOrganizationDetail = (org: OrganizationDetail) => {
    if (!dashboardData) return null;


    const allJobs: Array<{
      job: HungJob | EarlyFailureJob | LowParityJob;
      type: 'hung' | 'failed' | 'lowParity';
      role: 'source' | 'destination';
    }> = [];

    // Check hung jobs
    dashboardData.metrics.hungJobs?.forEach((job) => {
      if (job.sourceOrgId === org.orgId || job.sourceOrgName === org.orgName) {
        allJobs.push({ job, type: 'hung', role: 'source' });
      }
      if (job.destinationOrgId === org.orgId || job.destinationOrgName === org.orgName) {
        allJobs.push({ job, type: 'hung', role: 'destination' });
      }
    });

    // Check early failure jobs
    dashboardData.metrics.earlyFailureJobs?.forEach((job) => {
      if (job.sourceOrgId === org.orgId || job.sourceOrgName === org.orgName) {
        allJobs.push({ job, type: 'failed', role: 'source' });
      }
      if (job.destinationOrgId === org.orgId || job.destinationOrgName === org.orgName) {
        allJobs.push({ job, type: 'failed', role: 'destination' });
      }
    });

    // Check low parity jobs
    dashboardData.metrics.lowParityJobs?.forEach((job) => {
      if (job.sourceOrgId === org.orgId || job.sourceOrgName === org.orgName) {
        allJobs.push({ job, type: 'lowParity', role: 'source' });
      }
      if (job.destinationOrgName === org.orgName) {
        allJobs.push({ job, type: 'lowParity', role: 'destination' });
      }
    });

    // Remove duplicates (same transactionId)
    const uniqueJobs = Array.from(
      new Map(allJobs.map((item) => [item.job.transactionId, item])).values()
    );

    // Group by type
    const hungJobs = uniqueJobs.filter((j) => j.type === 'hung');
    const failedJobs = uniqueJobs.filter((j) => j.type === 'failed');
    const lowParityJobs = uniqueJobs.filter((j) => j.type === 'lowParity');

    return (
      <Stack gap={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h5">{org.orgName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Organization Details
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary">
                  Organization ID
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mt: 0.5 }}
                >
                  {org.orgId}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Organization Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {org.orgName}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Region
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {org.orgRegion || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Jobs Section */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <WorkIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">Jobs for {org.orgName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {uniqueJobs.length} job{uniqueJobs.length !== 1 ? 's' : ''} found
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {uniqueJobs.length === 0 ? (
              <Alert severity="info">No jobs found for this organization.</Alert>
            ) : (
              <Stack gap={3}>
                {/* Hung Jobs */}
                {hungJobs.length > 0 && (
                  <Box>
                    <DataGrid
                      title={`Hung Jobs (${hungJobs.length})`}
                      loading={loading}
                      columns={[
                        { key: 'transactionId', label: 'Transaction ID', width: 300, render: (r) => (
                          <Tooltip title={r.transactionId as string} arrow>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {(r.transactionId as string)}
                            </Typography>
                          </Tooltip>
                        )},
                        { key: 'role', label: 'Role', width: 100, render: (r) => (
                          <Chip 
                            size="small" 
                            label={r.role === 'source' ? 'Source' : 'Destination'} 
                            color={r.role === 'source' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                        )},
                        { key: 'sourceOrgName', label: 'Source', width: 180 },
                        { key: 'destinationOrgName', label: 'Destination', width: 180 },
                        { key: 'dateStarted', label: 'Started', width: 150, render: (r) => {
                          const started = new Date(r.dateStarted as string);
                          const hoursAgo = Math.floor((Date.now() - started.getTime()) / (1000 * 60 * 60));
                          return (
                            <Tooltip title={started.toLocaleString()} arrow>
                              <Chip size="small" label={`${hoursAgo}h ago`} color="error" variant="outlined" />
                            </Tooltip>
                          );
                        }},
                        { key: 'status', label: 'Status', width: 120, render: (r) => (
                          <Chip size="small" label={r.status as string || 'running'} color="error" icon={<WarningIcon sx={{ fontSize: 14 }} />} />
                        )},
                      ]}
                      data={hungJobs.map(({ job, role }) => ({
                        ...job,
                        role,
                      })) as unknown as Record<string, unknown>[]}
                      emptyMessage="No hung jobs"
                      emptyIcon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />}
                    />
                  </Box>
                )}

                {/* Failed Jobs */}
                {failedJobs.length > 0 && (
                  <Box>
                    <DataGrid
                      title={`Early Failure Jobs (${failedJobs.length})`}
                      loading={loading}
                      columns={[
                        { key: 'transactionId', label: 'Transaction ID', width: 300, render: (r) => (
                          <Tooltip title={r.transactionId as string} arrow>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {(r.transactionId as string)}
                            </Typography>
                          </Tooltip>
                        )},
                        { key: 'role', label: 'Role', width: 100, render: (r) => (
                          <Chip 
                            size="small" 
                            label={r.role === 'source' ? 'Source' : 'Destination'} 
                            color={r.role === 'source' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                        )},
                        { key: 'sourceOrgName', label: 'Source', width: 180 },
                        { key: 'destinationOrgName', label: 'Destination', width: 180 },
                        { key: 'status', label: 'Status', width: 100, render: (r) => (
                          <Chip size="small" label={r.status as string || 'failed'} color="error" />
                        )},
                        { key: 'failedSteps', label: 'Failed Steps', width: 300, render: (r) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {((r.failedSteps as string[]) || []).map((step, idx) => (
                              <Chip key={idx} size="small" label={step} color="error" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                            ))}
                          </Box>
                        )},
                        { key: 'successfullSteps', label: 'Completed', width: 120, render: (r) => {
                          const steps = r.successfullSteps as string[] | undefined;
                          if (!steps || steps.length === 0) return '-';
                          return (
                            <Tooltip title={steps.join(', ')} arrow>
                              <Chip size="small" label={`${steps.length} steps`} color="success" variant="outlined" />
                            </Tooltip>
                          );
                        }},
                        { key: 'dateStarted', label: 'Started', width: 180, render: (r) => new Date(r.dateStarted as string).toLocaleString() },
                      ]}
                      data={failedJobs.map(({ job, role }) => ({
                        ...job,
                        role,
                      })) as unknown as Record<string, unknown>[]}
                      emptyMessage="No early failures"
                      emptyIcon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />}
                    />
                  </Box>
                )}

                {/* Low Parity Jobs */}
                {lowParityJobs.length > 0 && (
                  <Box>
                    <DataGrid
                      title={`Low Parity Jobs (${lowParityJobs.length})`}
                      loading={loading}
                      columns={[
                        { key: 'transactionId', label: 'Transaction ID', width: 300, render: (r) => (
                          <Tooltip title={r.transactionId as string} arrow>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {(r.transactionId as string)}
                            </Typography>
                          </Tooltip>
                        )},
                        { key: 'role', label: 'Role', width: 100, render: (r) => (
                          <Chip 
                            size="small" 
                            label={r.role === 'source' ? 'Source' : 'Destination'} 
                            color={r.role === 'source' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                        )},
                        { key: 'sourceOrgName', label: 'Source', width: 180 },
                        { key: 'destinationOrgName', label: 'Destination', width: 180 },
                        { key: 'orgParityPercentage', label: 'Parity %', width: 220, render: (r) => {
                          const parity = parseFloat(r.orgParityPercentage as string);
                          return (
                            <Box sx={{ minWidth: 160 }}>
                              <ParityBar
                                copied={parity}
                                total={100}
                                isRunning={false}
                                totalBlocks={10}
                                showLoaderWhenRunning={false}
                              />
                            </Box>
                          );
                        }},
                        { key: 'dateStarted', label: 'Started', width: 180, render: (r) => new Date(r.dateStarted as string).toLocaleString() },
                      ]}
                      data={lowParityJobs.map(({ job, role }) => ({
                        ...job,
                        role,
                      })) as unknown as Record<string, unknown>[]}
                      emptyMessage="All jobs have healthy parity (>95%)"
                      emptyIcon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />}
                    />
                  </Box>
                )}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Stack>
    );
  };

  const renderOrgPairDetail = (pair: OrgPairAnalyticsItem) => {
    return (
      <Stack gap={2}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h5">
                  {pair.sourceOrgName} → {pair.destinationOrgName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Organization Pair Analytics
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Source Organization
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {pair.sourceOrgName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {pair.sourceOrgId}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Destination Organization
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {pair.destinationOrgName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {pair.destinationOrgId}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  Average Parity
                </Typography>
                <Box sx={{ mt: 0.5, minWidth: 160 }}>
                  <ParityBar
                    copied={parseFloat(pair.averageParityPercentage)}
                    total={100}
                    isRunning={false}
                    totalBlocks={10}
                    showLoaderWhenRunning={false}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  Job Count
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {pair.jobCount}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  P90 Duration
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {pair.p90DurationSeconds >= 3600
                    ? `${(pair.p90DurationSeconds / 3600).toFixed(1)}h`
                    : pair.p90DurationSeconds >= 60
                    ? `${Math.round(pair.p90DurationSeconds / 60)}m`
                    : `${Math.round(pair.p90DurationSeconds)}s`}
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={pair.lowParity ? 'Low Parity' : 'Healthy'}
                    size="small"
                    color={pair.lowParity ? 'warning' : 'success'}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Stack>
    );
  };


  return (
    <Box>
      <PageHeader
        title={`${type.charAt(0).toUpperCase() + type.slice(1)} Details`}
        subtitle={`Viewing details for ${type}`}
        actions={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            variant="outlined"
            size="small"
          >
            Back
          </Button>
        }
      />

      {entity.type === 'job' && renderJobDetail(entity.data as HungJob | EarlyFailureJob | LowParityJob, entity.jobType as 'hung' | 'earlyFailure' | 'lowParity')}
      {entity.type === 'organization' && renderOrganizationDetail(entity.data as OrganizationDetail)}
      {entity.type === 'orgPair' && renderOrgPairDetail(entity.data as OrgPairAnalyticsItem)}
    </Box>
  );
}

